import { Box, ScrollArea, Stack, Text } from '@mantine/core';
import {
  IconHeading,
  IconLayoutGrid,
  IconLetterT,
  IconMinus,
  IconVariable,
} from '@tabler/icons-react';

import { Block, ScopeComponent } from '@/types';
import { newBlockId } from './block-tree';
import classes from './TemplateEditor.module.css';

interface BlockPaletteProps {
  scopeComponents: ScopeComponent[];
  // Called when an item is clicked. The page handles inserting via the
  // BlockEditor's setTree/append helper.
  onInsert: (block: Block) => void;
}

export function BlockPalette({ scopeComponents, onInsert }: Readonly<BlockPaletteProps>) {
  return (
    <ScrollArea h="100%">
      <div className={classes.palette}>
        <Text className={classes.paletteGroupLabel}>Structure</Text>

        <PaletteItem
          icon={<IconHeading size={14} />}
          label="Heading"
          description="A section heading (H1–H6)"
          onInsert={() => onInsert({ id: newBlockId(), kind: 'heading', level: 2, content: [] })}
        />
        <PaletteItem
          icon={<IconLetterT size={14} />}
          label="Paragraph"
          description="Prose with @-mention variables"
          onInsert={() => onInsert({ id: newBlockId(), kind: 'paragraph', content: [] })}
        />
        <PaletteItem
          icon={<IconMinus size={14} />}
          label="Divider"
          description="Horizontal line"
          onInsert={() => onInsert({ id: newBlockId(), kind: 'divider' })}
        />

        <Text className={classes.paletteGroupLabel}>Data</Text>

        <PaletteItem
          icon={<IconVariable size={14} />}
          label="Variable"
          description="Standalone variable with optional filters"
          onInsert={() => onInsert({ id: newBlockId(), kind: 'variable', path: '', filters: [] })}
        />

        {scopeComponents.length > 0 && (
          <>
            <Text className={classes.paletteGroupLabel}>Sections</Text>
            {scopeComponents.map((sc) => (
              <PaletteItem
                key={sc.id}
                icon={<IconLayoutGrid size={14} />}
                label={sc.name}
                description={sc.description ?? sc.slug}
                onInsert={() => onInsert({ id: newBlockId(), kind: 'section', slug: sc.slug })}
              />
            ))}
          </>
        )}
      </div>
    </ScrollArea>
  );
}

interface PaletteItemProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  onInsert: () => void;
}

function PaletteItem({ icon, label, description, onInsert }: Readonly<PaletteItemProps>) {
  return (
    <Box
      className={classes.paletteItem}
      role="button"
      tabIndex={0}
      onClick={onInsert}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onInsert();
        }
      }}
    >
      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {icon}
          <Text size="sm" fw={500} lineClamp={1}>
            {label}
          </Text>
        </div>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {description}
        </Text>
      </Stack>
    </Box>
  );
}
